/**
 * useTableSort — hold which sort a table is on, and apply it.
 *
 * Sorting is a list of named options with their own comparators rather than a
 * column id plus a direction, because the useful orders are not one per column
 * and not all ascending: a roster sorts by name A–Z but by every figure
 * descending, since "most" is the question being asked of a number.
 *
 * The chosen option is kept BY ID and looked up, so an option disappearing —
 * `withoutUnbuilt` drops the ones whose feature does not exist yet — leaves the
 * list unsorted rather than crashing on a comparator that is no longer there.
 */
import { useCallback, useMemo, useState } from 'react';

export interface TableSortOption<Row> {
  id: string;
  label: string;
  compare: (a: Row, b: Row) => number;
}

export interface TableSort<Row> {
  sortId: string | undefined;
  setSortId: (id: string) => void;
  options: readonly TableSortOption<Row>[];
  /** `rows` in the chosen order. Never sorts in place. */
  sorted: (rows: readonly Row[]) => Row[];
}

export function useTableSort<Row>(
  options: readonly TableSortOption<Row>[],
  defaultId?: string,
): TableSort<Row> {
  const [sortId, setSortId] = useState<string | undefined>(defaultId ?? options[0]?.id);
  const active = useMemo(() => options.find((option) => option.id === sortId), [options, sortId]);

  // Both the comparator and the object around it are memoised. Callers put this
  // in a `useMemo` dependency list to derive their rows, so a fresh identity per
  // render means those rows are rebuilt on every keystroke and every hover — and
  // a component downstream that treats a new array as new DATA (the venue map
  // pushing pins) then does real work for nothing.
  const sorted = useCallback(
    (rows: readonly Row[]) => (active ? [...rows].sort(active.compare) : [...rows]),
    [active],
  );

  return useMemo(() => ({ sortId, setSortId, options, sorted }), [sortId, options, sorted]);
}
